import { useState, useEffect } from "react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { logger } from "@/services/Logger";

let audioContext: AudioContext | null = null;
let audioBuffer: AudioBuffer | null = null;
let notificationSound: HTMLAudioElement | null = null;

const initAudioContext = async () => {
  try {
    if (!audioContext) {
      // Support for webkit-prefixed AudioContext in older browsers
      const AudioContextClass =
        window.AudioContext || (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      audioContext = new AudioContextClass();

      if (!audioBuffer) {
        const response = await fetch("/notification.wav");
        const arrayBuffer = await response.arrayBuffer();
        audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      }
    }

    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }

    return true;
  } catch (error) {
    logger.error("useNotificationPermission", "Failed to initialize audio context:", error);
    return false;
  }
};

const initAudioElement = () => {
  if (!notificationSound) {
    notificationSound = new Audio("/notification.wav");
    notificationSound.load(); // Preload the sound
  }
};

const playWithAudioContext = async () => {
  if (!audioContext || !audioBuffer) {
    const initialized = await initAudioContext();
    if (!initialized) return false;
  }

  try {
    const source = audioContext!.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioContext!.destination);
    source.start(0);
    return true;
  } catch (error) {
    logger.error("useNotificationPermission", "Error playing with AudioContext:", error);
    return false;
  }
};

const playWithAudioElement = () => {
  try {
    if (!notificationSound) {
      initAudioElement();
    }

    notificationSound!.currentTime = 0;
    const playPromise = notificationSound!.play();

    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        logger.error("useNotificationPermission", "Audio element playback error:", error);
        return false;
      });
    }

    return true;
  } catch (error) {
    logger.error("useNotificationPermission", "Error with audio element:", error);
    return false;
  }
};

const requestNotificationPermission = async (): Promise<boolean> => {
  if (!("Notification" in window)) {
    logger.info(
      "requestNotificationPermission",
      "This browser does not support notifications"
    );
    return false;
  }

  if (Notification.permission === "granted") {
    return true;
  }

  try {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  } catch (error) {
    logger.error("useNotificationPermission", "Error requesting notification permission:", error);
    return false;
  }
};

const registerServiceWorker =
  async (): Promise<ServiceWorkerRegistration | null> => {
    if (!("serviceWorker" in navigator)) {
      logger.info("registerServiceWorker", "Service Worker not supported");
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      return registration;
    } catch (error) {
      logger.error("useNotificationPermission", "Error accessing service worker registration:", error);
      return null;
    }
  };

const showSystemNotification = async (
  title: string,
  options: NotificationOptions = {}
): Promise<boolean> => {
  if (!("Notification" in window)) {
    return false;
  }

  if (Notification.permission === "granted") {
    const notificationOptions = {
      icon: "/favicon.ico",
      badge: "/favicon.ico",
      ...options,
    };

    try {
      // Check if we're in a PWA/mobile context or have access to serviceWorker
      if ("serviceWorker" in navigator) {
        const registration = await registerServiceWorker();
        if (registration) {
          await registration.showNotification(title, notificationOptions);
          return true;
        }
      }

      // Fall back to standard Notification API if not in PWA context
      // This will work in desktop browsers but may fail in PWAs
      try {
        const notification = new Notification(title, notificationOptions);
        notification.onclick = function () {
          window.focus();
          notification.close();
        };
        return true;
      } catch (error) {
        logger.error("useNotificationPermission", "Error showing standard notification:", error);
        return false;
      }
    } catch (error) {
      logger.error("useNotificationPermission", "Error showing notification:", error);
      return false;
    }
  }

  return false;
};

export const sendNotification = async (
  title: string,
  body: string,
  playSound = true
) => {
  let soundPlayed = false;

  if (playSound) {
    soundPlayed = await playWithAudioContext();

    if (!soundPlayed) {
      soundPlayed = playWithAudioElement();
    }
  }

  const notificationShown = await showSystemNotification(title, {
    body,
    silent: soundPlayed,
  });

  return { soundPlayed, notificationShown };
};

export const useNotificationPermission = () => {
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [permissionChecked, setPermissionChecked] = useState<boolean>(false);
  const confirm = useConfirm();

  useEffect(() => {
    const storedPermission = localStorage.getItem("notificationPermission");
    const browserPermission =
      "Notification" in window ? Notification.permission : "default";

    if (browserPermission === "granted") {
      setHasPermission(true);
      localStorage.setItem("notificationPermission", "granted");
    } else if (storedPermission === "granted") {
      setHasPermission(true);
    } else if (browserPermission === "denied") {
      setHasPermission(false);
      localStorage.setItem("notificationPermission", "denied");
    } else if (storedPermission === "denied") {
      setHasPermission(false);
    } else {
      setHasPermission(null);
    }

    setPermissionChecked(true);
  }, []);

  const requestPermission = async () => {
    try {
      await initAudioContext();

      const result = await requestNotificationPermission();

      // Register service worker if permission granted
      if (result && "serviceWorker" in navigator) {
        try {
          await registerServiceWorker();
        } catch (e) {
          logger.error("useNotificationPermission", "Error registering service worker:", e);
        }
      }

      setHasPermission(result);
      localStorage.setItem(
        "notificationPermission",
        result ? "granted" : "denied"
      );

      return result;
    } catch (error) {
      logger.error("useNotificationPermission", "Error requesting permission:", error);
      return false;
    }
  };

  const showPermissionDialog = async () => {
    const ok = await confirm({
      title: "Activer les notifications",
      description: "Voulez-vous activer les notifications pour ScaniFood ?",
      confirmText: "Oui, activer",
      cancelText: "Non",
    });
    if (ok) {
      const permissionGranted = await requestPermission();
      if (permissionGranted) {
        (await playWithAudioContext()) || playWithAudioElement();
      }
      return permissionGranted;
    }
    setHasPermission(false);
    localStorage.setItem("notificationPermission", "denied");
    return false;
  };

  return {
    hasPermission,
    permissionChecked,
    requestPermission,
    showPermissionDialog,
    sendNotification,
  };
};
